import { Backdrop, CircularProgress, Container, Grid } from "@material-ui/core";
import { ArrowBack } from "@material-ui/icons";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import TextField from "../../components/Form/TextField";
import ButtonUI from "../../components/ui/ButtonUI";
import PageTitle from "../../components/ui/PageTitleUI";
import { useAuth } from "../../hooks/auth";
import ShippingRequestApi from "../../services/ShippingRequestApi";
import utilsDate from "../../utils/Date";
import { useStyles } from "../../utils/DefaultStyles";
import permissions from "../../utils/Permissions";
import LogList from "./LogList";
import PartNumberList from "./PartNumberList";

interface IFormInput {
    supplierCorporateName: string;
    supplierObservation: string;
    programmerObservation: string;
    invoiceProgrammerObservation: string;
    partNumbers: any;
    logs: any;
    protocolNumber: any;
    plantId: string;
    companyId: string;
    requestDate: any;
    supplierId: number;
    partDumpHeaderId: number;
    status: string;
}

const ShippingRequestView: React.FC = () => {
    const classes = useStyles();
    const { user, hasPermission } = useAuth();
    const { t: ts } = useTranslation("ShippingRequest");
    const { t: tGeneral } = useTranslation("General");
    const { enqueueSnackbar } = useSnackbar();
    const history = useHistory();
    const [circularLoading, setCircularLoading] = useState(false);
    const [title, setTitle] = useState<string>("");
    const [shippingRequestStatus, setShippingRequestStatus] = useState<string>("");

    const { control, setValue } =
        useForm<IFormInput>({
            defaultValues: {
                requestDate: utilsDate.DateShort(new Date()),
            },
        });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!hasPermission(permissions.SHIPPING_REQUEST.View))
            history.replace('NoPermission');
    }, [history, user]);

    useEffect(() => {
        setCircularLoading(true);

        var id = Number(
            window.location.pathname.replace("/ShippingRequest/View/", "")
        );

        if (isNaN(id)) {
            enqueueSnackbar(tGeneral("objectNotFound"), {
                variant: "warning",
            });
            history.replace("/ShippingRequest")
        }

        ShippingRequestApi.get(id).then((response) => {
            if (response) {
                setTitle(`${ts("viewTitle")}`)
                
                setValue("partNumbers", response.partNumbers);
                setValue("protocolNumber", response.protocolNumber);
                setValue("supplierCorporateName", response.supplierId + " - " + response.supplierCorporateName);
                setValue("supplierObservation", response.supplierObservation);
                setValue("programmerObservation", response.programmerObservation);
                setValue("invoiceProgrammerObservation", response.invoiceProgrammerObservation);
                setValue("logs", response.logs);
                setValue("status", response.statusDescription);
                setShippingRequestStatus(response.status);
                setValue("requestDate", utilsDate.DateShort(new Date(response.requestDate)));

                setCircularLoading(false);
            } else {
                setCircularLoading(false);
                enqueueSnackbar(tGeneral("objectNotFound"), {
                    variant: "warning",
                });
            }
        }).finally(() => {
            setCircularLoading(false);
        });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [window.location.pathname]);

    return (
        <Container maxWidth={false} component="main">
            <PageTitle title={title} />
            <div className={classes.rootContent}>
                <div className={classes.paper}>

                    <Backdrop style={{ zIndex: 9999 }} open={circularLoading}>
                        <CircularProgress color="inherit" />
                    </Backdrop>

                    <form>
                        <Grid container spacing={2}>
                            <Grid item xs={1}>
                                <TextField
                                    label={ts("protocol")}
                                    name="protocolNumber"
                                    control={control}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        readOnly: true,
                                        classes: {
                                            input: classes.inputBlocked,
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    label={ts("supplier")}
                                    name="supplierCorporateName"
                                    control={control}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        readOnly: true,
                                        classes: {
                                            input: classes.inputBlocked,
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={1}>
                                <TextField
                                    label={ts("requestDate")}
                                    name="requestDate"
                                    control={control}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        readOnly: true,
                                        classes: {
                                            input: classes.inputBlocked,
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    label={ts("status")}
                                    name="status"
                                    control={control}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        readOnly: true,
                                        classes: {
                                            input: classes.inputBlocked,
                                        },
                                    }}
                                />
                            </Grid>
                        </Grid>
                        <Grid container spacing={1}>
                            <Grid item xs={8}>
                                <TextField
                                    label={ts("supplierObservation")}
                                    name="supplierObservation"
                                    control={control}
                                    disable={true}
                                    variant={"outlined"}
                                    multiline={true}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                        <Grid container spacing={1}>
                            <Grid item xs={8}>
                                <TextField
                                    label={ts("programmerObservation")}
                                    name="programmerObservation"
                                    control={control}
                                    disable={true}
                                    variant={"outlined"}
                                    multiline={true}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                        <Grid container spacing={1}>
                            <Grid item xs={8}>
                                <TextField
                                    label={ts("invoiceProgrammerObservation")}
                                    name="invoiceProgrammerObservation"
                                    control={control}
                                    disable={true}
                                    variant={"outlined"}
                                    multiline={true}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </form>

                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            {
                                <PartNumberList
                                    control={control}
                                    name={"partNumbers"}
                                    status={shippingRequestStatus}
                                    allowAdd={false}
                                />
                            }
                        </Grid>
                    </Grid>

                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            {<LogList control={control} name={"logs"} />}
                        </Grid>
                    </Grid>

                    <Grid container spacing={1}>
                        <Grid
                            item
                            xs={12}
                            style={{
                                padding: "0px",
                                textAlign: "left",
                                marginTop: "30px",
                                marginLeft: "5px",
                            }}
                        >
                            <Backdrop
                                style={{ zIndex: 9999 }}
                                open={circularLoading}
                            >
                                <CircularProgress color="inherit" />
                            </Backdrop>

                            <ButtonUI
                                variant="outlined"
                                color="secondary"
                                onClick={() =>
                                    history.replace("/ShippingRequest")
                                }
                                startIcon={<ArrowBack />}
                            >
                                {tGeneral("back")}
                            </ButtonUI>
                        </Grid>
                    </Grid>
                </div>
            </div>
        </Container>
    );
};
export default ShippingRequestView;
